// Сессия мастера в клиенте (D8, D35).
//
// Здесь закрыты все три слоя гонки, из-за которой в предыдущем
// проекте разлогинивало через пятнадцать минут. Слои разные, и лечатся
// они порознь — поэтому и стоят порознь, каждый со своим тестом:
//
// 1. **Публичные процедуры мимо проверки.** На сервере белого списка
//    путей нет вовсе: процедура либо требует владельца, либо нет
//    (`requireUser` в `apps/api/src/trpc.ts`), и «забыть добавить путь
//    в список» негде. В клиенте список один — `PUBLIC`, ниже.
// 2. **Токен на `auth.*`.** Отправлять протухший токен туда, где его
//    не проверяют, — верный способ получить отказ на обновлении
//    и уйти в цикл. `accessTokenFor` не кладёт токен на публичные
//    процедуры, `mayRefresh` не даёт по ним обновляться.
// 3. **Параллельные запросы.** Десять вкладок, проснувшихся разом,
//    обязаны дать одно обновление, а не десять. За это отвечает
//    `inflight` — единственный живой промис обновления.
//
// Хранение (D35): refresh — в `localStorage`, access — только
// в памяти вкладки. Размен не в пользу `localStorage`, и он объяснён
// в самом решении: кука `httpOnly` недоступна, пока фронт и API стоят
// на разных доменах (D16).

import {
  opaqueTokenSchema,
  type AuthProfile,
  type AuthTokens,
} from '@rollcall/contracts'
import type { SessionPort } from '../api.js'

export const REFRESH_STORAGE_KEY = 'rollcall.refresh'

/**
 * Процедуры, которые ходят без токена и не обновляются по отказу.
 *
 * `auth.me` и `auth.adoptDrafts` сюда не входят намеренно: они как раз
 * требуют вошедшего, и по их отказу обновиться — ровно то, что нужно.
 */
export const PUBLIC_PROCEDURES = [
  'health',
  'auth.register',
  'auth.confirmEmail',
  'auth.login',
  'auth.refresh',
  'auth.logout',
  'auth.requestPasswordReset',
  'auth.resetPassword',
] as const

const PUBLIC = new Set<string>(PUBLIC_PROCEDURES)

export function isPublicProcedure(path: string): boolean {
  return PUBLIC.has(path)
}

/** Хранилище: то немногое от localStorage, что здесь нужно. */
export type SessionStorage = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>

/** Что клиенту нужно от сервера, чтобы вести сессию. */
export type SessionTransport = {
  refreshTokens(refreshToken: string): Promise<AuthTokens>
  logout(refreshToken: string, allDevices: boolean): Promise<number>
}

export type SessionListener = (profile: AuthProfile | null) => void

export type Session = SessionPort & {
  /** Профиль вошедшего или `null`. */
  profile(): AuthProfile | null
  /** Есть ли что восстанавливать: refresh-токен в хранилище. */
  restorable(): boolean
  /** Принять токены после входа. */
  accept(tokens: AuthTokens, profile: AuthProfile): void
  /**
   * Проставить профиль отдельно от токенов. Нужно ровно в одном
   * месте: при восстановлении сессии на старте токены приходят
   * обновлением, а профиль — следующим запросом.
   */
  setProfile(profile: AuthProfile | null): void
  /** Выйти по-настоящему: погасить сессию на сервере и забыть токены. */
  signOut(allDevices: boolean): Promise<void>
  /** Подписка на смену состояния: экрану нужно перерисоваться. */
  subscribe(listener: SessionListener): () => void
}

export function createSession(
  transport: SessionTransport,
  storage: SessionStorage,
): Session {
  let access: string | null = null
  let refreshToken: string | null = read(storage)
  let profile: AuthProfile | null = null
  let inflight: Promise<boolean> | null = null
  const listeners = new Set<SessionListener>()

  const announce = (): void => {
    for (const listener of listeners) listener(profile)
  }

  const forget = (): void => {
    access = null
    refreshToken = null
    profile = null
    try {
      storage.removeItem(REFRESH_STORAGE_KEY)
    } catch {
      // Хранилище недоступно (приватный режим Safari): токен и так
      // жил только в памяти, и её мы уже очистили.
    }
    announce()
  }

  const accept = (tokens: AuthTokens, next: AuthProfile): void => {
    access = tokens.accessToken
    refreshToken = tokens.refreshToken
    profile = next
    write(storage, tokens.refreshToken)
    announce()
  }

  /**
   * Одно обновление на любое число ждущих.
   *
   * Промис создаётся один раз и раздаётся всем: десять запросов,
   * упавших на протухшем токене, ждут его же. Сбрасывается он
   * отдельной подпиской, а не в `finally` у вызывающего, — иначе
   * первый же дождавшийся освободил бы место следующему, и обновлений
   * снова стало бы десять.
   */
  const refresh = (): Promise<boolean> => {
    if (inflight === null) {
      inflight = renew()
      const clear = (): void => {
        inflight = null
      }
      // Отказ здесь уже обработан внутри `renew`: наружу выходит
      // только `false`. Подписка нужна ради сброса, а не ради ошибки.
      void inflight.then(clear, clear)
    }
    return inflight
  }

  const renew = async (): Promise<boolean> => {
    if (refreshToken === null) return false
    try {
      const tokens = await transport.refreshTokens(refreshToken)
      access = tokens.accessToken
      refreshToken = tokens.refreshToken
      write(storage, tokens.refreshToken)
      return true
    } catch {
      // Отказ обновления — это конец сессии, а не повод попробовать
      // ещё раз. Токены забываются здесь же: оставить мёртвый
      // refresh-токен в хранилище означает повторять этот отказ
      // на каждом следующем запросе.
      forget()
      return false
    }
  }

  return {
    accessTokenFor(path) {
      return isPublicProcedure(path) ? null : access
    },
    mayRefresh(path) {
      return !isPublicProcedure(path) && refreshToken !== null
    },
    refresh,
    forget,
    profile: () => profile,
    restorable: () => refreshToken !== null,
    accept,
    async signOut(allDevices) {
      const token = refreshToken
      // Токены забываются в любом случае, даже если сервер недоступен:
      // «выйти не получилось, вы всё ещё в аккаунте» — не тот ответ,
      // которого ждут от кнопки выхода.
      forget()
      if (token === null) return
      try {
        await transport.logout(token, allDevices)
      } catch {
        // Сессия на сервере доживёт свой срок сама (D35).
      }
    },
    setProfile(next) {
      profile = next
      announce()
    },
    subscribe(listener) {
      listeners.add(listener)
      return () => {
        listeners.delete(listener)
      }
    },
  }
}

/**
 * Токен из хранилища проверяется схемой, а не берётся на веру: мусор
 * (чужая версия, ручная правка) означал бы запрос с заведомо мёртвым
 * токеном и выход без объяснения — то же соображение, что и с токеном
 * устройства (`device.ts`).
 */
function read(storage: SessionStorage): string | null {
  try {
    const parsed = opaqueTokenSchema.safeParse(storage.getItem(REFRESH_STORAGE_KEY))
    return parsed.success ? parsed.data : null
  } catch {
    return null
  }
}

function write(storage: SessionStorage, token: string): void {
  try {
    storage.setItem(REFRESH_STORAGE_KEY, token)
  } catch {
    // Приватный режим Safari: сессия проживёт до перезагрузки вкладки.
  }
}
